// EngineCore — the shared inner driver used by every embedder of the
// drawing core:
//
//   1. the Web Worker (`drawingWorker.ts` in `@oekaki/web`) when
//      `OffscreenCanvas` is available,
//   2. the main-thread fallback inside `CanvasDrawingEngine` (used in
//      tests and on browsers / contexts where `OffscreenCanvas` is
//      unavailable),
//   3. the Phase 5 server-side snapshot worker (`@oekaki/server`'s
//      `SnapshotEngine`, backed by `@napi-rs/canvas`).
//
// This file owns the glue between `StrokeRenderer` and a 2D canvas-like
// surface — no `HTMLCanvasElement`, no `Worker`. Anything that wants to be
// pixel-correct uses the same code through this seam, so the snapshot
// worker (Phase 5) gets bit-perfect identity with the browser path for
// free. (architecture.md §11 / §19)
import { ThumbnailScheduler, } from './layerThumbnails.js';
import { createLayer } from './rasterizer.js';
import { rasterizeLeadSegment, StrokeRenderer, } from './strokeRenderer.js';
/**
 * 長押し強調 (layer-thumbnails-2026-10-13.md): while a layer is "solo"ed the other
 * layers are shown at this fraction (times their own display opacity). 15〜25% を
 * 実機で見比べて決める — 定数はここ 1 箇所。
 */
export const LAYER_SOLO_OTHERS_OPACITY = 0.2;
/** Per-layer display opacity range (layer-display-opacity-2026-10-12.md: 0 は作らない、見えなくするのは 👁). */
export const MIN_LAYER_OPACITY = 0.1;
export const MAX_LAYER_OPACITY = 1;
/** Clamp to `[MIN_LAYER_OPACITY, 1]`; non-finite input → `null` (caller ignores). */
export const clampLayerOpacity = (v) => {
    if (!Number.isFinite(v))
        return null;
    return Math.min(MAX_LAYER_OPACITY, Math.max(MIN_LAYER_OPACITY, v));
};
const DEFAULT_BACKGROUND = { rgb: 0xffffff, alpha: 255 };
/**
 * EngineCore binds a `StrokeRenderer` to a `DisplayBackend` and provides
 * the small API the worker / main-thread engine forwards into. All
 * methods are synchronous; the engine is responsible for any rAF
 * batching it wants on top.
 */
export class EngineCore {
    opts;
    renderer;
    backend = null;
    background;
    /** Phase 7 Task 7-8a — see `startImagepasteOverlay`. */
    overlay = null;
    /**
     * 表示専用リード線オーバーレイ (modern-layout.md §9.8.1 ①). imagepaste overlay
     * と同型 (straight-α RGBA + bbox + layerId)。**`Layer.main` には絶対書かない**:
     * `flush` / `repaint` の display 合成で、imagepaste overlay の **更に上 (最前面)**
     * に毎回 source-over するだけ。前フレームのリードは次の display 再合成で自然に
     * 消える (= imagepaste preview と同原理、stroke buffer 非汚染が構造的に保証)。
     *
     * 手ぶれ補正 OFF / Classic では `setLead` が呼ばれない (リード=ゼロ長) ので
     * `null` のまま = 完全 no-op。
     */
    lead = null;
    /**
     * Per-layer display visibility (= W3 Classic レイヤー追加, 2026-05-26).
     *
     * **Client-local state only** — the 👁 表示トグル in the Classic floating
     * layer popup is a "個人ビュー" for peeking at layers underneath; it is
     * NOT broadcast and NOT carried in the protocol (docs/_wip/
     * w3-classic-layers.md §2.3).
     *
     * Indexed by `layerId`. `true` = composite this layer into the **display**
     * output (`flush` / `repaint`). A hidden layer is skipped only at display
     * time; its pixels remain intact in `Layer.main`.
     *
     * **Export / snapshot / spuit IGNORE this array entirely** — capture and
     * snapshot are session-global artifacts that must not be skewed by one
     * client's local view (`compositeForExport` / `compositeAllForExport` /
     * `getLayerPixelColor` never read `visibility`). The task spec is explicit
     * on this split.
     */
    visibility;
    /**
     * Per-layer **display** opacity (layer-display-opacity-2026-10-12.md), each in
     * `[MIN_LAYER_OPACITY, 1]`, default 1. Client-local like `visibility` and
     * independent of it (hide + show keeps the value). Multiplied into the
     * DISPLAY composite only (`composeDisplayLayers`); export / snapshot
     * (`compositeForExport` / `compositeAllForExport`) and spuit mode A
     * (`compositeVisibleWithBg`) never read it — spuit must return the real
     * colour, not a dimmed one.
     */
    opacity;
    /**
     * Active drag preview (`beginOpacityPreview`). While non-null the display
     * is owned by the backend's preview surfaces: `flush` / `repaint` / region
     * repaints are suppressed (layer state still updates normally) and
     * `endOpacityPreview` repaints once. `opacity[]` is NOT touched until commit.
     */
    opacityPreview = null;
    /**
     * Active 長押し強調 (`beginLayerSolo`). Reuses the opacity-preview backend
     * surfaces (3 sheets, pre-dimmed) and the same display suppression; mutually
     * exclusive with `opacityPreview` and an imagepaste overlay. Display-only:
     * `Layer.main` / `opacity[]` / `visibility[]` are never touched.
     */
    layerSolo = null;
    /**
     * 範囲の移動で「浮かせている間、元の範囲の絵を手元で隠す」ための表示専用マスク
     * (`setLocalRegionMask`). `composeDisplayLayers` が対象レイヤーの該当矩形を透明として
     * 合成するだけで、`Layer.main` / snapshot / undo / サムネ / export には一切効かない。
     */
    localRegionMask = null;
    /**
     * Layer thumbnails (layer-thumbnails-2026-10-13.md). `null` = feature OFF: the
     * only residual cost is the renderer's per-layer dirty bbox bookkeeping.
     */
    thumbs = null;
    /**
     * The scheduler gave up (very slow device): sticky for this core's lifetime
     * (= the tab). A later `setLayerThumbnails(config)` (user setting OFF -> ON)
     * reports stopped again instead of restarting.
     */
    thumbsAutoStopped = false;
    /** sids of strokes THIS client is drawing (pen down … up); see `hasOwnStroke`. */
    ownSids = new Set();
    constructor(opts) {
        this.opts = opts;
        const layers = [];
        const layerCount = opts.layerCount ?? 1;
        this.visibility = new Array(layerCount).fill(true);
        this.opacity = new Array(layerCount).fill(1);
        for (let i = 0; i < layerCount; i++) {
            // Layers are transparent-default (= 0,0,0,0). The canvas background is
            // a separate property composited under the layers at display / export
            // time. This separation is what unblocks β multi-layer (each layer is
            // its own surface; bg lives below them all) and fixes the α 公開時に
            // 発覚した 3 症状 (= キャプの黒焼き付き / スポイトで消しゴム跡=黒 /
            // 将来 layer の白塗り破綻). Detail: docs/_wip/layer-background-
            // separation-2026-05-17.md.
            layers.push(createLayer(i, opts.width, opts.height));
        }
        this.background = opts.background ?? DEFAULT_BACKGROUND;
        this.renderer = new StrokeRenderer(layers);
    }
    /**
     * Replace the canvas background.
     *
     * **No display repaint** (2026-06-20, canvas-background-color memo §display):
     * the display compose no longer burns in the bg — the flat bg color is
     * emitted by the page's CSS background-color, which the frontend updates
     * directly. So changing `background` here only needs to update the property
     * that **export / snapshot** (`compositeForExport` / `compositeAllForExport`)
     * reads. This is the whole point of the change: the owner dragging the bg
     * color no longer queues a full-canvas re-composite per frame.
     *
     * Still used by `applySnapshot` (snapshot meta.json carries the historical
     * bg) and by `whiteboard.background-set` broadcasts so saved artifacts and
     * spuit sampling keep using the up-to-date bg.
     */
    setBackground(next) {
        this.background = next;
    }
    /** Current canvas background — for tests / snapshot meta.json writing. */
    getBackground() {
        return this.background;
    }
    /** Attach a backend; flushes a full repaint immediately. */
    attachBackend(backend) {
        this.backend = backend;
        this.repaint();
    }
    detachBackend() {
        if (this.opacityPreview !== null) {
            this.opacityPreview = null;
            this.backend?.previewEnd?.();
        }
        if (this.layerSolo !== null) {
            this.layerSolo = null;
            this.backend?.previewEnd?.();
        }
        this.backend = null;
    }
    /** Underlying renderer — exposed for tests + snapshot generation. */
    getRenderer() {
        return this.renderer;
    }
    /**
     * Restrict every subsequent stamp / erase write to `clip` (inclusive
     * layer-space rect) until cleared with `null`. Forwards to
     * `StrokeRenderer.setWriteClip` — see that method's header for the full
     * contract (conflict-aware Undo B' bbox-local repair,
     * docs/_wip/undo-conflict-aware-2026-07-12.md「stamp 書き込みの bbox
     * clip」). Only `UndoBaselineStore`'s dedicated scratch `EngineCore` ever
     * calls this; the live drawing engine never does.
     */
    setWriteClip(clip) {
        this.renderer.setWriteClip(clip);
    }
    /** Layer access for tests / Phase 5 snapshot. */
    getLayer(layerId) {
        return this.renderer.layers[layerId];
    }
    /** Number of allocated layers (= `layerCount` from construction / resize). */
    getLayerCount() {
        return this.renderer.layers.length;
    }
    /**
     * Set a layer's **display** visibility (W3 Classic レイヤー追加 §2.3).
     *
     * Client-local only: toggling 👁 in the Classic layer popup hides the
     * layer from THIS client's display composite (`flush` / `repaint`) so the
     * user can peek at the layers below. It is NOT broadcast and does NOT
     * touch the layer's pixels — `Layer.main` is untouched, so unhiding shows
     * the same content, and export / snapshot / spuit always include the layer
     * regardless of this flag.
     *
     * Out-of-range `layerId` is a silent no-op. Triggers a full repaint when
     * the value actually changes (so the backend picks up the layer
     * appearing / disappearing).
     */
    setLayerVisible(layerId, visible) {
        if (layerId < 0 || layerId >= this.renderer.layers.length)
            return;
        this.endLayerSolo();
        if (this.visibility[layerId] === visible)
            return;
        this.visibility[layerId] = visible;
        this.repaint();
    }
    /** Current display visibility for `layerId` (default true / unknown → false). */
    isLayerVisible(layerId) {
        return this.visibility[layerId] ?? false;
    }
    /**
     * Set a layer's **display** opacity (layer-display-opacity-2026-10-12.md).
     * Clamped to `[0.1, 1]`; NaN / out-of-range `layerId` is a silent no-op.
     * Display only: Layer.main / export / snapshot / spuit mode A are unaffected.
     * Repaints (once) when the value actually changes. An active drag preview is
     * cancelled first (`endOpacityPreview(false)`), so a direct set always wins.
     */
    setLayerOpacity(layerId, opacity) {
        if (layerId < 0 || layerId >= this.renderer.layers.length)
            return;
        const v = clampLayerOpacity(opacity);
        if (v === null)
            return;
        if (this.opacityPreview !== null)
            this.endOpacityPreview(false);
        this.endLayerSolo();
        if (this.opacity[layerId] === v)
            return;
        this.opacity[layerId] = v;
        this.repaint();
    }
    /** Confirmed display opacity for `layerId` (unknown layer → 1). */
    getLayerOpacity(layerId) {
        return this.opacity[layerId] ?? 1;
    }
    /** Snapshot of every layer's confirmed display opacity (index = layerId). */
    getLayerOpacities() {
        return this.opacity.slice();
    }
    /** Reset every layer to 100% with a single repaint (the 「うすく表示中」札). */
    resetLayerOpacities() {
        if (this.opacityPreview !== null)
            this.endOpacityPreview(false);
        this.endLayerSolo();
        let changed = false;
        for (let i = 0; i < this.opacity.length; i++) {
            if (this.opacity[i] !== 1) {
                this.opacity[i] = 1;
                changed = true;
            }
        }
        if (changed)
            this.repaint();
    }
    /**
     * Start a drag preview for `layerId`'s opacity. Builds the three sheets ONCE
     * (visible layers below / the layer alone / visible layers above, other
     * layers at their confirmed opacity) and hands them to the backend; every
     * `updateOpacityPreview` then only redraws via drawImage. A preview already
     * active (same or other layer) is cancelled first. Returns false (no preview)
     * for an unknown layer.
     *
     * The lead line is not part of the sheets (no drawing happens mid-drag);
     * in-flight strokes are folded into the sheets as at begin time.
     */
    beginOpacityPreview(layerId) {
        if (layerId < 0 || layerId >= this.renderer.layers.length)
            return false;
        if (this.layerSolo !== null)
            return false; // 強調中は始めない
        if (this.opacityPreview !== null)
            this.endOpacityPreview(false);
        this.opacityPreview = { layerId, value: this.opacity[layerId] ?? 1 };
        const layer0 = this.renderer.layers[0];
        const backend = this.backend;
        if (backend?.previewBegin !== undefined && layer0 !== undefined) {
            const full = { x0: 0, y0: 0, x1: layer0.width - 1, y1: layer0.height - 1 };
            const confirmed = (id) => this.opacity[id] ?? 1;
            const pick = (include, opacityOf) => this.composeDisplayLayers(full, { include, opacityOf })?.rgba ?? null;
            backend.previewBegin({
                width: layer0.width,
                height: layer0.height,
                below: pick((id) => id < layerId, confirmed),
                target: pick((id) => id === layerId, () => 1),
                above: pick((id) => id > layerId, confirmed),
            });
        }
        return true;
    }
    /** Move the previewed opacity (clamped). No-op without an active preview. */
    updateOpacityPreview(opacity) {
        const p = this.opacityPreview;
        if (p === null)
            return;
        const v = clampLayerOpacity(opacity);
        if (v === null)
            return;
        p.value = v;
        this.backend?.previewDraw?.(v);
    }
    /** The active preview (layer + current value) or null — for tests / UI. */
    getOpacityPreview() {
        return this.opacityPreview === null ? null : { ...this.opacityPreview };
    }
    /**
     * Finish the drag. `commit=true` makes the previewed value the layer's
     * opacity; `false` keeps the old one. Either way the display is repainted
     * ONCE through the normal path (which also catches up on everything that was
     * suppressed during the drag). No-op without an active preview.
     */
    endOpacityPreview(commit) {
        const p = this.opacityPreview;
        if (p === null)
            return;
        this.opacityPreview = null;
        if (commit)
            this.opacity[p.layerId] = p.value;
        this.backend?.previewEnd?.();
        this.repaint();
    }
    /** True while the backend's preview surfaces own the display (drag preview or solo). */
    displayHeld() {
        return this.opacityPreview !== null || this.layerSolo !== null;
    }
    /**
     * 長押し強調: show `layerId` at 100% (even when 👁-hidden or dimmed) and every
     * other layer at `LAYER_SOLO_OTHERS_OPACITY` × its own display opacity (👁-hidden
     * others stay hidden). The canvas background is never part of the display
     * sheets (CSS emits it), so it is untouched. Builds the 3 sheets ONCE; nothing
     * is redrawn per frame. Returns false (no change) for an unknown layer, no
     * backend, or while a drag opacity preview / imagepaste overlay is active.
     * Beginning on another layer while soloing switches layers.
     */
    beginLayerSolo(layerId) {
        if (layerId < 0 || layerId >= this.renderer.layers.length)
            return false;
        if (this.opacityPreview !== null || this.overlay !== null)
            return false;
        const layer0 = this.renderer.layers[0];
        const backend = this.backend;
        if (backend === null || layer0 === undefined)
            return false;
        if (this.layerSolo !== null)
            this.endLayerSolo();
        this.layerSolo = layerId;
        if (backend.previewBegin !== undefined) {
            const full = { x0: 0, y0: 0, x1: layer0.width - 1, y1: layer0.height - 1 };
            const dimmed = (id) => (this.opacity[id] ?? 1) * LAYER_SOLO_OTHERS_OPACITY;
            const pick = (include, opacityOf, visibleOf) => this.composeDisplayLayers(full, { include, opacityOf, visibleOf })?.rgba ?? null;
            backend.previewBegin({
                width: layer0.width,
                height: layer0.height,
                below: pick((id) => id < layerId, dimmed),
                target: pick((id) => id === layerId, () => 1, () => true),
                above: pick((id) => id > layerId, dimmed),
            });
            backend.previewDraw?.(1);
        }
        return true;
    }
    /**
     * 表示だけ、指定レイヤーの指定矩形 (canvas 座標) を透明として描く。`null` で解除。
     * `Layer.main` は変えない (送らない・snapshot / undo / サムネ / export に影響しない)。
     * 表示濃度・👁・長押し強調・描画中の合成と併用でき、変更時に全面再描画を 1 回行う。
     */
    setLocalRegionMask(mask) {
        const next = mask === null || mask.layerIds.length === 0 || mask.rect.w <= 0 || mask.rect.h <= 0
            ? null
            : {
                layerIds: new Set(mask.layerIds),
                x: mask.rect.x,
                y: mask.rect.y,
                w: mask.rect.w,
                h: mask.rect.h,
            };
        if (next === null && this.localRegionMask === null)
            return;
        this.localRegionMask = next;
        this.repaint();
    }
    /** The active display-only mask (for tests / UI), or null. */
    getLocalRegionMask() {
        const m = this.localRegionMask;
        return m === null
            ? null
            : { layerIds: [...m.layerIds].sort((a, b) => a - b), x: m.x, y: m.y, w: m.w, h: m.h };
    }
    /** The soloed layer id, or null — for tests / UI. */
    getLayerSolo() {
        return this.layerSolo;
    }
    /** Leave 長押し強調 with ONE normal repaint (catches up on what was suppressed). No-op when inactive. */
    endLayerSolo() {
        if (this.layerSolo === null)
            return;
        this.layerSolo = null;
        this.backend?.previewEnd?.();
        this.repaint();
    }
    /**
     * Sample a single pixel's RGB from the **exported (= bg-composited)** image
     * of the given layer. Used by the Classic UI スポイト (eyedropper) feature
     * triggered via right-click / Alt+click on the canvas (旧版踏襲、新版で
     * は Phase 6.7 後追い実装、consult 2026-05-06).
     *
     * Semantics:
     *
     *   - Returns the same color the user **visually sees** at `(x, y)` —
     *     `compositeForExport(layerId)` composites the canvas background
     *     under the layer pixels, so an eraser-cleared / transparent pixel
     *     reads as the bg color (not (0,0,0)). This fixes the α 公開時に
     *     発覚した「スポイトで消しゴム跡を吸うと黒が拾われる」症状 (=
     *     transparent + bg=white should sample white, not black).
     *   - In-flight `'flat'` / `'marker'` stroke buffers ARE included because
     *     `compositeForDisplay` (which `compositeForExport` builds on) already
     *     folds them in for the §19.6 display path. Picking what's visually
     *     on screen is the right UX.
     *   - Alpha channel of the picked pixel is intentionally **dropped** —
     *     the spuit feature copies RGB to the palette only and leaves the
     *     current pen alpha unchanged (旧版 `new Color(col)` 動作踏襲).
     *
     * Returns `null` when:
     *   - `layerId` is out of range,
     *   - `(x, y)` is out of layer bounds (caller's responsibility to clamp
     *     if it wants edge sampling — for the spuit UX, OOB = "clicked the
     *     letterbox", a no-op).
     *
     * Coordinates are floored to integer pixel indices (matches the legacy
     * `getRGB(x, y)` semantics — sub-pixel sampling would require interpolation
     * which the spuit UX doesn't need).
     *
     * **Cost note**: this allocates and composites one full-canvas RGBA
     * buffer per call. spuit is a low-rate UI gesture (≤ pointermove rate
     * even during drag-spuit), so the overhead is invisible. If it ever
     * becomes hot the bg + layer compose for a single pixel could be
     * inlined here.
     */
    getLayerPixelColor(layerId, x, y) {
        const layer = this.renderer.layers[layerId];
        if (layer === undefined)
            return null;
        const ix = Math.floor(x);
        const iy = Math.floor(y);
        if (ix < 0 || iy < 0 || ix >= layer.width || iy >= layer.height)
            return null;
        const composed = this.compositeForExport(layerId);
        const idx = (iy * layer.width + ix) * 4;
        const r = composed[idx];
        const g = composed[idx + 1];
        const b = composed[idx + 2];
        if (r === undefined || g === undefined || b === undefined)
            return null;
        return { r, g, b };
    }
    /**
     * Sample a single pixel for the spuit (eyedropper) feature, with the source
     * selected by `mode` (spuit-sample-modes-2026-06-22 §2/§3). Supersedes
     * `getLayerPixelColor` (kept for back-compat = mode `'layer-bg'` minus α).
     *
     * Returns `{ r, g, b, a }`:
     *
     *   - `'display'`  (A) — `compositeVisibleWithBg()`: the **visible** layer
     *     stack under the canvas background, hidden layers skipped, lead overlay
     *     excluded. `(x, y)` is read from this full-canvas composite. `a` is a
     *     meaningless fixed 255 (mode A drops α — the handler copies RGB only).
     *   - `'layer'`    (B) — `renderer.compositeForDisplay(layerId)`: the raw
     *     single layer (NO bg), with in-flight `'flat'`/`'marker'` stroke buffers
     *     folded in. The real **α is returned** so the handler can switch to the
     *     eraser on `a === 0` and otherwise adopt RGB only.
     *   - `'layer-bg'` (C) — `compositeForExport(layerId)`: the selected layer
     *     under the bg (= legacy spuit). `a` is a meaningless fixed 255.
     *
     * Mode A composites the whole visible stack and so does not depend on the
     * specific `layerId` for its color; modes B and C read the given `layerId`.
     * The bounds / OOB check always uses `layerId`'s layer in every mode (an OOB
     * click outside the canvas is a no-op everywhere, and an unknown `layerId`
     * returns `null` everywhere — same contract as the legacy method).
     *
     * Returns `null` when `layerId` is out of range or `(x, y)` is out of layer
     * bounds. Coordinates are floored to integer pixel indices.
     *
     * **Cost note** (carried over from `getLayerPixelColor`): each call allocates
     * + composites one full-canvas RGBA buffer. spuit is a low-rate UI gesture
     * and drag-spuit is coalesced (`SpuitPickCoalescer`, in-flight ≤ 1), so the
     * overhead is invisible even for the heavier mode A visible-stack composite.
     */
    samplePixel(mode, layerId, x, y) {
        const layer = this.renderer.layers[layerId];
        if (layer === undefined)
            return null;
        const ix = Math.floor(x);
        const iy = Math.floor(y);
        if (ix < 0 || iy < 0 || ix >= layer.width || iy >= layer.height)
            return null;
        let composed;
        switch (mode) {
            case 'display':
                // Mode A: 全可視レイヤー + bg (見たまま). NEVER `compositeAllForExport`
                // (= 非表示込み) — hidden layers must not be sampled (§3).
                composed = this.compositeVisibleWithBg();
                break;
            case 'layer':
                // Mode B: 選択レイヤーの生ピクセル (bg 合成しない, in-flight 含む). α を保持.
                composed = this.renderer.compositeForDisplay(layerId);
                break;
            case 'layer-bg':
                // Mode C: 選択レイヤー + bg (= legacy `getLayerPixelColor`).
                composed = this.compositeForExport(layerId);
                break;
        }
        const idx = (iy * layer.width + ix) * 4;
        const r = composed[idx];
        const g = composed[idx + 1];
        const b = composed[idx + 2];
        if (r === undefined || g === undefined || b === undefined)
            return null;
        // Mode B returns the real α; A/C drop α (fixed 255, meaningless — handler
        // copies RGB only). Reading the composite's own α for B is correct: it is
        // the layer's straight-α (bg not composited), so α=0 = 透明ピクセル.
        const a = mode === 'layer' ? (composed[idx + 3] ?? 0) : 255;
        return { r, g, b, a };
    }
    startStroke(args) {
        const { own, ...rest } = args;
        // My own pen going down ends 長押し強調 at once (描き味最優先): the repaint
        // happens before the first segment is drawn.
        if (own === true)
            this.endLayerSolo();
        this.renderer.startStroke({ ...rest, nowMs: rest.nowMs ?? Date.now() });
        if (own === true) {
            this.ownSids.add(args.sid);
            this.thumbs?.noteInput();
        }
        else {
            this.thumbs?.noteRemoteActivity();
        }
    }
    addPoints(sid, points, nowMs) {
        this.renderer.addPoints(sid, points, nowMs ?? Date.now());
        this.flush();
        // Own strokes never need the scheduler (it is held off until pen-up);
        // a peer's commit may have dirtied a layer.
        if (this.thumbs !== null && !this.ownSids.has(sid)) {
            this.thumbs.noteRemoteActivity();
            this.thumbs.kick();
        }
    }
    endStroke(sid) {
        this.renderer.endStroke(sid);
        this.flush();
        this.afterStrokeEnd(sid);
    }
    // ─── Layer thumbnails ────────────────────────────────────────────────────
    /**
     * Turn the thumbnail pipeline on (`onThumbnail` receives each finished layer
     * thumbnail) or off (`null`). Turning it on starts a full rebuild, scheduled
     * like any other (not before the quiet period, in ≤1 ms slices). OFF drops the
     * pipeline entirely; the renderer's per-layer dirty bookkeeping keeps running
     * (a handful of ints) and is superseded by the full rebuild at the next ON.
     */
    setLayerThumbnails(config) {
        if (this.thumbs !== null) {
            this.thumbs.dispose();
            this.thumbs = null;
        }
        if (config === null)
            return;
        if (this.thumbsAutoStopped) {
            config.onStopped?.();
            return;
        }
        this.thumbs = new ThumbnailScheduler({
            source: {
                layers: () => this.renderer.layers,
                takeLayerDirty: (id) => this.renderer.takeLayerDirty(id),
                hasLayerDirty: () => this.renderer.hasLayerDirty(),
                hasOwnStroke: () => this.hasOwnStroke(),
                hasRemoteStroke: () => this.hasRemoteStroke(),
            },
            onThumbnail: config.onThumbnail,
            onStopped: () => {
                this.thumbsAutoStopped = true;
                this.thumbs = null;
                config.onStopped?.();
            },
            ...(config.visible !== undefined ? { visible: config.visible } : {}),
            ...(config.now !== undefined ? { now: config.now } : {}),
            ...(config.setTimer !== undefined ? { setTimer: config.setTimer } : {}),
            ...(config.clearTimer !== undefined ? { clearTimer: config.clearTimer } : {}),
        });
    }
    /** True once the thumbnails were stopped automatically (never reset). */
    isThumbnailsAutoStopped() {
        return this.thumbsAutoStopped;
    }
    /** The thumbnail scheduler while ON (tests / worker stats), else null. */
    getThumbnailScheduler() {
        return this.thumbs;
    }
    /**
     * "Hold off" from the main thread (the view is being moved — the same
     * `suspended` the preview thumbnail uses). Idle when thumbnails are OFF.
     */
    setThumbnailsSuspended(suspended) {
        this.thumbs?.setSuspended(suspended);
    }
    /**
     * Whether any surface currently shows thumbnails. `false`: per-layer dirty
     * tracking continues but nothing is built; `true`: the backlog is rebuilt in
     * the usual small slices. Idle when thumbnails are OFF.
     */
    setThumbnailsVisible(visible) {
        this.thumbs?.setVisible(visible);
    }
    /** Main thread's rAF-jitter measurement (share of slow frames) feeding the back-off tier. */
    reportThumbnailMainJank(ratio) {
        this.thumbs?.reportMainJank(ratio);
    }
    /** Is a peer's stroke in flight? */
    hasRemoteStroke() {
        const total = this.renderer.activeStrokeCount();
        if (total === 0)
            return false;
        let own = 0;
        for (const sid of this.ownSids)
            if (this.renderer.hasStroke(sid))
                own++;
        return total > own;
    }
    /** Is one of this client's own strokes still alive in the renderer? Prunes dead sids. */
    hasOwnStroke() {
        if (this.ownSids.size === 0)
            return false;
        for (const sid of this.ownSids) {
            if (this.renderer.hasStroke(sid))
                return true;
            this.ownSids.delete(sid); // dropped without an end (clear / resize)
        }
        return false;
    }
    afterStrokeEnd(sid) {
        if (this.thumbs === null) {
            this.ownSids.delete(sid);
            return;
        }
        if (this.ownSids.delete(sid)) {
            this.thumbs.noteInput();
        }
        else {
            this.thumbs.noteRemoteActivity();
            this.thumbs.kick();
        }
    }
    clearLayer(layerId) {
        // Clear to transparent. The canvas background is a separate property
        // (see ctor): at display time the cleared (transparent) layer shows the
        // page's CSS background-color through, and at export time the bg property is
        // composited under the layer (`compositeForExport`), so the user still sees
        // the bg color after 全消し in both views. The layer is genuinely empty
        // (no inherited bg pixels that would leak into snapshots / spuit / capture).
        this.renderer.clearLayer(layerId, 0x000000, 0);
        this.repaint();
        this.thumbs?.kick();
    }
    resize(width, height) {
        // Early return when dimensions don't change. Without this, a redundant
        // resize() arriving AFTER a stroke replay (e.g. welcome.deltaEvents
        // applied via applyRemoteStroke before the DrawingCanvas useEffect that
        // calls engine.resize() runs) wipes every layer to the background and
        // blanks the canvas. The previous design always rebuilt the layer
        // bitmaps regardless of whether the dims actually changed; the worker
        // message queue ordering (filter.set → stroke.* → resize) made that
        // unsafe in the snapshot-less mid-join path.
        if (this.opts.width === width && this.opts.height === height)
            return;
        // A drag preview's sheets are sized for the old canvas — cancel it.
        if (this.opacityPreview !== null)
            this.endOpacityPreview(false);
        this.endLayerSolo();
        this.opts = { ...this.opts, width, height };
        const layers = [];
        const layerCount = this.renderer.layers.length;
        for (let i = 0; i < layerCount; i++) {
            // Transparent-default (see ctor for why bg is composited separately,
            // not baked into the layer bitmap).
            layers.push(createLayer(i, width, height));
        }
        this.renderer = new StrokeRenderer(layers);
        this.ownSids.clear();
        // Keep the per-layer visibility array aligned with the (unchanged) layer
        // count. resize never changes layerCount, but be defensive: preserve
        // existing flags, default any new index to visible. (W3 §2.3)
        const nextVisibility = new Array(layerCount).fill(true);
        for (let i = 0; i < layerCount; i++) {
            nextVisibility[i] = this.visibility[i] ?? true;
        }
        this.visibility = nextVisibility;
        if (this.backend !== null) {
            this.backend.resize(width, height);
            this.repaint();
        }
        this.thumbs?.rebuildAll();
    }
    /**
     * Force-end any stroke whose last activity is older than the cutoff.
     * Used by the in-engine watchdog (architecture.md §19.7) so a remote
     * peer's `alpha`-pen stroke doesn't stay stuck in the buffer forever.
     */
    forceEndIdle(cutoffMs) {
        const ended = this.renderer.forceEndIdle(cutoffMs);
        if (ended.length > 0) {
            this.flush();
            for (const sid of ended)
                this.afterStrokeEnd(sid);
        }
        return ended;
    }
    /**
     * Replace each layer's `main` bitmap with snapshot pixels and trigger a
     * full repaint. Used by the Phase 5 snapshot path (architecture.md §15)
     * after the client has fetched + decoded `meta.json` + `layer-N.png` for
     * `room.welcome.snapshot` / `room.resume-ok.snapshot`.
     *
     * Per-layer `rgba` MUST match the engine's current `width * height * 4`.
     * Mismatched layers are silently skipped — the caller is responsible for
     * resizing the engine first if the snapshot dims differ.
     *
     * In-flight stroke buffers are intentionally **left alone**: a local
     * user mid-stroke at the moment a snapshot lands continues to draw on
     * top of the new pixels, with their `endStroke` commit applied to the
     * snapshot main as expected. Snapshots from the server only carry
     * committed (post-`stroke.end`) pixels, so there is no double-apply.
     *
     * The pixel buffer is **copied** into the layer; the caller may free the
     * source bitmap immediately on return.
     *
     * `resetAll` (past-canvas「続きを描く」ライブ復元, 2026-07-12 hotfix): when
     * `true`, clear EVERY layer to transparent (and drop any in-flight stroke
     * buffer targeting them) BEFORE blitting the seed layers, atomically within
     * this call. The restore path uses this so a layer that exists in the
     * current session but is absent from the seed snapshot doesn't survive with
     * stale pixels — and, crucially, so the wipe can't race a separately-queued
     * `clear` (the blank-canvas ordering bug). Omitted / `false` keeps the
     * welcome / resume behaviour: only the listed layers are replaced.
     */
    applySnapshot(snapshotLayers, resetAll = false) {
        if (resetAll) {
            // Clear all layers to transparent + drop in-flight strokes targeting
            // them (same wipe as `whiteboard.clear` over every layer). This is the
            // non-pixel + pixel cleanup the restore handler used to route through
            // `applyRemoteStroke([clear])`, now folded into the atomic seed apply.
            this.renderer.clearLayer(undefined, 0x000000, 0);
        }
        for (const snap of snapshotLayers) {
            const layer = this.renderer.layers[snap.id];
            if (layer === undefined)
                continue;
            const expected = layer.width * layer.height * 4;
            if (snap.rgba.length !== expected)
                continue;
            layer.main.set(snap.rgba);
            this.renderer.markLayerDirty(snap.id, {
                x0: 0,
                y0: 0,
                x1: layer.width - 1,
                y1: layer.height - 1,
            });
        }
        this.repaint();
        this.thumbs?.kick();
    }
    /**
     * Patch a rectangular region of `layer.main` directly with caller-supplied
     * RGBA bytes and repaint just that region (conflict-aware Undo B' bbox-local
     * rewind, docs/_wip/undo-conflict-aware-2026-07-12.md 「クライアント側の復元
     * コスト」/ protocol-events.md §1.1). This is the "巻き戻し replay は通常描画
     * と同一パイプライン" seam: `UndoBaselineStore` computes the repaired region
     * off-engine (baseline + intersecting-stroke replay on a scratch surface)
     * and hands the resulting bytes here; this method only does the blit +
     * display refresh, it never re-derives pixels itself.
     *
     * `rgba` length MUST equal `w * h * 4`; a mismatch is a silent no-op
     * (defensive — mirrors `applySnapshot`'s per-layer length guard). `(x0,
     * y0)` + `(w, h)` are clamped to the layer bounds; a region entirely
     * outside the layer is a no-op.
     *
     * No in-flight stroke buffer interaction: this only touches the committed
     * `layer.main` bitmap, exactly like `applySnapshot`. The B' design
     * guarantees (conflict-aware Undo's `conflict` rejection) that no
     * in-flight stroke's growing bbox can intersect a region under rewind, so
     * there is nothing "in flight" to reconcile here.
     */
    applyLayerRegion(layerId, x0, y0, w, h, rgba) {
        const layer = this.renderer.layers[layerId];
        if (layer === undefined)
            return;
        if (w <= 0 || h <= 0)
            return;
        if (rgba.length !== w * h * 4)
            return;
        const cx0 = Math.max(0, x0);
        const cy0 = Math.max(0, y0);
        const cx1 = Math.min(layer.width - 1, x0 + w - 1);
        const cy1 = Math.min(layer.height - 1, y0 + h - 1);
        if (cx1 < cx0 || cy1 < cy0)
            return;
        const rowBytes = (cx1 - cx0 + 1) * 4;
        for (let y = cy0; y <= cy1; y++) {
            const srcRow = ((y - y0) * w + (cx0 - x0)) * 4;
            const dstRow = (y * layer.width + cx0) * 4;
            layer.main.set(rgba.subarray(srcRow, srcRow + rowBytes), dstRow);
        }
        this.renderer.markDirty({ x0: cx0, y0: cy0, x1: cx1, y1: cy1 });
        this.renderer.markLayerDirty(layerId, { x0: cx0, y0: cy0, x1: cx1, y1: cy1 });
        this.flush();
        this.thumbs?.kick();
    }
    /**
     * 範囲の移動・コピー (docs/_wip/region-move-2026-10-15.md): 層の「素の絵」
     * (`layer.main` = straight-alpha RGBA) を矩形で写し取る。背景・👁・表示濃度・
     * 強調・遅延 commit ペンの in-flight バッファは含めない (見たままではなく、確定済みの
     * ピクセルそのもの)。矩形は層の範囲に切り捨てる。戻りの `rgba` は新しいバッファ
     * (transferable として返してよい)。層が無い / 完全に範囲外なら `null`。
     * 戻りの `x/y/width/height` は切り捨て後の矩形。
     */
    readLayerRegion(layerId, x, y, w, h) {
        const layer = this.renderer.layers[layerId];
        if (layer === undefined)
            return null;
        const c = clampRegionToLayer(x, y, w, h, layer.width, layer.height);
        if (c === null)
            return null;
        const rgba = new Uint8ClampedArray(c.w * c.h * 4);
        const rowBytes = c.w * 4;
        for (let row = 0; row < c.h; row++) {
            const start = ((c.y + row) * layer.width + c.x) * 4;
            rgba.set(layer.main.subarray(start, start + rowBytes), row * rowBytes);
        }
        return { x: c.x, y: c.y, width: c.w, height: c.h, rgba };
    }
    /**
     * 矩形を (0,0,0,0) にする (`clearLayer` と同じ値)。矩形は層の範囲に切り捨てる。
     * 描きかけ (in-flight) の線は落とさない: 遅延 commit のペンは stroke.end の seq が
     * この命令より後なので、server の snapshot (seq 順) でも消去の後に載る。
     */
    clearRegion(layerId, x, y, w, h) {
        const layer = this.renderer.layers[layerId];
        if (layer === undefined)
            return;
        const c = clampRegionToLayer(x, y, w, h, layer.width, layer.height);
        if (c === null)
            return;
        const rowBytes = c.w * 4;
        for (let row = 0; row < c.h; row++) {
            const start = ((c.y + row) * layer.width + c.x) * 4;
            layer.main.fill(0, start, start + rowBytes);
        }
        const bbox = { x0: c.x, y0: c.y, x1: c.x + c.w - 1, y1: c.y + c.h - 1 };
        this.renderer.markDirty(bbox);
        this.renderer.markLayerDirty(layerId, bbox);
        this.flush();
        this.thumbs?.kick();
    }
    /**
     * Push the dirty bbox to the backend (no-op if backend isn't attached).
     *
     * 2026-05-17 (PR 2-α `compositeForDisplay` の dirty-rect 化): the previous
     * implementation called `compositeForExport(layerId)` which allocates and
     * composites a full-canvas RGBA buffer (= 64 MB on 4000² canvas + the
     * `compositeForDisplay` heavy path's own 64 MB copy of `layer.main`),
     * then `subarray`-copied the dirty bbox out. Trace analysis at α tier
     * (= `docs/_wip/perf-baseline-alpha-workload-2026-05-17.md`) showed
     * compositor dominating frame budget (1700-1900 ms per stroke). The new
     * path composites bg + layer + in-flight strokes + overlay **only** for
     * the dirty bbox, dropping per-rAF cost from O(canvas area) to O(bbox
     * area). `repaint()` still uses the full-canvas `compositeForExport`
     * because it explicitly wants the whole surface (= snapshot / spuit /
     * resize / init); `flush` is the rAF hot path that benefits from the
     * region-limited path.
     */
    flush() {
        if (this.backend === null)
            return;
        // Layer-opacity drag in progress: the backend's preview surfaces own the
        // display. Keep the dirty bbox accumulating; `endOpacityPreview` repaints.
        if (this.displayHeld())
            return;
        const bbox = this.renderer.flushDirtyBbox();
        if (bbox.x0 < 0)
            return;
        const layers = this.renderer.layers;
        if (layers.length === 0)
            return;
        // W3 Classic レイヤー追加 (2026-05-26): composite EVERY visible layer in
        // stack order (id0 背 → id1 中 → id2 前) for the dirty sub-rect, over the
        // canvas background, then the imagepaste overlay on its target layer.
        //
        // Display-only visibility (§2.3): a hidden layer is skipped here so the
        // user can peek at layers below. The layer's pixels are untouched, and
        // export / snapshot ignore this flag.
        //
        // Cost stays O(bbox area × visibleLayerCount): each layer's region
        // composite restricts both alloc and iteration to the dirty bbox (the
        // PR 2-α dirty-rect win). For the Classic 3-layer config that's ≤3×
        // the single-layer path, still O(bbox) not O(canvas).
        //
        // We use the FIRST layer's region as the accumulation buffer (bg under
        // it) and source-over each subsequent visible layer on top. Clamping the
        // dirty bbox to layer bounds is identical across same-sized layers, so
        // `(x, y, w, h)` from the first region matches every layer's region.
        const acc = this.composeDisplayLayers(bbox);
        if (acc === null) {
            // Every layer in the dirty bbox is hidden / empty: paint just the bg so
            // the backend doesn't keep showing stale pixels where a now-hidden
            // layer used to be.
            const layer = layers[0];
            if (layer === undefined)
                return;
            const clamped = clampBboxToLayer(bbox, layer.width, layer.height);
            if (clamped === null)
                return;
            const { x, y, w, h } = clamped;
            const buf = new Uint8ClampedArray(new ArrayBuffer(w * h * 4));
            // Display compose never burns in the bg (2026-06-20): the buffer stays
            // transparent over the now-empty region; the CSS background shows through.
            // 表示専用リード線 (§9.8.1): the lead sits frontmost, above every layer +
            // the imagepaste overlay. A per-segment commit's flush over the stroke's
            // dirty bbox may overlap the lead rect — re-compositing it here keeps the
            // lead visible across the body advancing (= imagepaste と同じ「毎フレーム
            // source から再合成」原理). The lead's OWN move/clear repaint is handled
            // separately in `repaintLeadRegion`; this just preserves it under other
            // flushes.
            this.compositeLeadInto(buf, x, y, w, h);
            this.backend.put(buf, x, y, w, h);
            return;
        }
        // 表示専用リード線 (§9.8.1) — frontmost, see the all-hidden branch above.
        this.compositeLeadInto(acc.rgba, acc.x, acc.y, acc.w, acc.h);
        this.backend.put(acc.rgba, acc.x, acc.y, acc.w, acc.h);
    }
    /**
     * In-place source-over of the canvas background under the (transparent-
     * default) layer pixels held in `target`. Mirrors the bg-blend half of
     * `compositeForExport` but operates on a sub-rect (length = `w * h * 4`)
     * rather than the full canvas — the dirty-rect path uses this to keep
     * the per-rAF iteration cost O(bbox area) instead of O(canvas area).
     *
     * The pixel-level result is bit-identical to the full-canvas
     * `compositeForExport` for any pixel in the sub-rect.
     *
     * `applyBg` (default `true`) gates the bg compose stage. Since the 2026-06-20
     * change, **only the export/snapshot path composites the bg** (passes `true`);
     * the display path no longer calls this helper to add a bg — the flat bg
     * color is emitted by the page's CSS background-color instead. The
     * `compositeStack` all-hidden fallback passes `applyBg = !respectVisibility`,
     * so display (`false`) leaves `target` transparent (= 透明) while export
     * (`true`) burns in the bg.
     */
    composeBackgroundInto(target, w, h, applyBg = true) {
        if (!applyBg)
            return;
        const bgR = (this.background.rgb >> 16) & 0xff;
        const bgG = (this.background.rgb >> 8) & 0xff;
        const bgB = this.background.rgb & 0xff;
        const bgA = this.background.alpha;
        const bgAlpha = bgA / 255;
        const len = w * h * 4;
        for (let i = 0; i < len; i += 4) {
            const sr = target[i] ?? 0;
            const sg = target[i + 1] ?? 0;
            const sb = target[i + 2] ?? 0;
            const sa = target[i + 3] ?? 0;
            if (sa === 255) {
            }
            else if (sa === 0) {
                // Layer transparent — show bg as-is.
                target[i] = bgR;
                target[i + 1] = bgG;
                target[i + 2] = bgB;
                target[i + 3] = bgA;
            }
            else {
                const alpha = sa / 255;
                const outA = alpha + bgAlpha * (1 - alpha);
                if (outA <= 0) {
                    target[i] = 0;
                    target[i + 1] = 0;
                    target[i + 2] = 0;
                    target[i + 3] = 0;
                }
                else {
                    target[i] = Math.round((sr * alpha + bgR * bgAlpha * (1 - alpha)) / outA);
                    target[i + 1] = Math.round((sg * alpha + bgG * bgAlpha * (1 - alpha)) / outA);
                    target[i + 2] = Math.round((sb * alpha + bgB * bgAlpha * (1 - alpha)) / outA);
                    target[i + 3] = Math.round(outA * 255);
                }
            }
        }
    }
    /**
     * Repaint the whole canvas from layer state. Use after init / clear /
     * resize / backend reattach / visibility toggle.
     *
     * W3 Classic レイヤー追加 (2026-05-26): composites EVERY visible layer in
     * stack order (id0 背 → id1 中 → id2 前), applying the imagepaste overlay on
     * its target layer. Hidden layers are skipped (display-only; export / snapshot
     * ignore visibility). The full-canvas path is fine here because `repaint` is
     * the cold path (init / clear / resize / toggle), not the per-rAF hot path
     * (= `flush`).
     *
     * **No canvas background** (2026-06-20): the display surface is the
     * transparent-default layer stack alone — the flat bg color is emitted by the
     * page's CSS background-color. Unpainted areas stay transparent (alpha 0).
     */
    repaint() {
        if (this.backend === null)
            return;
        if (this.displayHeld())
            return; // see `flush`
        const layer0 = this.renderer.layers[0];
        if (layer0 === undefined)
            return;
        const composed = this.compositeVisibleForDisplay();
        this.backend.put(composed, 0, 0, layer0.width, layer0.height);
        // Clear the dirty bbox since we've just drawn everything.
        this.renderer.flushDirtyBbox();
    }
    /**
     * Full-canvas composite of the **visible** layer stack (NO canvas background,
     * 2026-06-20 — the bg is emitted by CSS), with the imagepaste overlay applied
     * on its target layer. This is the display-time view (= what `repaint` blits +
     * what the display canvas exposes via `toDataURL`). Hidden layers are skipped
     * (§2.3); unpainted areas stay transparent.
     *
     * Distinct from `compositeAllForExport` (which ignores visibility — used
     * for snapshot / capture-all). Both share the same bottom-to-top
     * source-over math; the only difference is the visibility gate.
     */
    compositeVisibleForDisplay() {
        // Display path: visible layers only, NO bg (the flat bg color is emitted by
        // the page's CSS background-color, 2026-06-20). The lead overlay is included
        // (display only).
        return this.compositeStack(true, false);
    }
    /**
     * Composite the **visible** layer stack over the canvas background into a
     * single full-canvas RGBA buffer — used by the spuit mode A ("表示色を取得",
     * spuit-sample-modes-2026-06-22 §2). This is the missing third combination:
     * the display path is (visible, no bg) and the export-all path is (all, bg);
     * mode A needs (visible, bg) = "what the user actually sees" = visible layers
     * composited under the canvas background, with hidden layers skipped.
     *
     * Distinct from `compositeAllForExport` (which IGNORES visibility, so it would
     * wrongly pick up a layer the user hid via 👁). The 2-axis `compositeStack`
     * makes this a one-liner without touching the existing two callers.
     *
     * The lead overlay is intentionally NOT included here (`respectVisibility`
     * gates it inside `compositeStack`, and we pass `false`): spuit samples the
     * committed picture, not the transient手ぶれ補正 lead hint — matching the
     * export semantics of mode C / the previous spuit behaviour.
     */
    compositeVisibleWithBg() {
        return this.compositeStack(true, true);
    }
    /**
     * The ONE display-stack compositor (layer-display-opacity-2026-10-12.md): for
     * the clamped `bbox`, source-over every visible layer bottom-to-top, each
     * scaled by its display opacity, with the imagepaste overlay applied on its
     * target layer (scaled by that layer's opacity too, so confirming a paste
     * doesn't pop). Used by `flush`, `repaintImagepasteRegion`,
     * `repaintLeadRegion`, the full-canvas display composite (`repaint`) and the
     * opacity-preview sheets. No canvas bg (CSS emits it), no lead (callers add
     * it frontmost). Returns `null` when no visible layer contributes a region.
     *
     * Opacity math: a layer with opacity < 1 has its straight-α alpha channel
     * multiplied before it is laid on the stack (bottom layer: scaled in place on
     * the region copy — `compositeForDisplayRegion` copies, but if a region ever
     * aliases `Layer.main` it is copied first so the layer is never mutated;
     * opacity === 1 layers skip the multiply entirely).
     *
     * `opts.include` filters layers (preview sheets); `opts.opacityOf` overrides
     * the opacity source (target sheet uses 1 — the drag applies it via
     * `globalAlpha`).
     */
    composeDisplayLayers(bbox, opts) {
        const layers = this.renderer.layers;
        let acc = null;
        for (let id = 0; id < layers.length; id++) {
            const visible = opts?.visibleOf !== undefined ? opts.visibleOf(id) : this.visibility[id];
            if (visible === false)
                continue;
            if (opts?.include !== undefined && !opts.include(id))
                continue;
            let region = this.renderer.compositeForDisplayRegion(id, bbox);
            if (region.w === 0 || region.h === 0)
                continue;
            region = this.applyLocalRegionMask(id, region, layers[id]?.main);
            const op = opts?.opacityOf !== undefined ? opts.opacityOf(id) : (this.opacity[id] ?? 1);
            if (acc === null) {
                // Bottom-most visible layer. Display compose NEVER burns in the canvas
                // background (2026-06-20): the page's CSS background-color shows through.
                if (op < 1) {
                    if (region.rgba === layers[id]?.main) {
                        region = { ...region, rgba: new Uint8ClampedArray(region.rgba) };
                    }
                    scaleAlphaInPlace(region.rgba, op);
                }
                acc = region;
            }
            else {
                sourceOverRegion(acc.rgba, region.rgba, op);
            }
            // Overlay sits on top of its target layer; the `layerId` gate inside makes
            // it composite EXACTLY once across the loop.
            this.compositeOverlayInto(acc.rgba, acc.x, acc.y, acc.w, acc.h, id, op);
        }
        return acc;
    }
    /** `localRegionMask` を region (alpha=0 に) へ適用。`Layer.main` を指していたら先にコピーする。 */
    applyLocalRegionMask(id, region, main) {
        const m = this.localRegionMask;
        if (m === null || !m.layerIds.has(id))
            return region;
        const x0 = Math.max(m.x, region.x);
        const y0 = Math.max(m.y, region.y);
        const x1 = Math.min(m.x + m.w, region.x + region.w);
        const y1 = Math.min(m.y + m.h, region.y + region.h);
        if (x0 >= x1 || y0 >= y1)
            return region;
        let out = region;
        if (region.rgba === main) {
            out = { ...region, rgba: new Uint8ClampedArray(region.rgba) };
        }
        for (let y = y0; y < y1; y++) {
            let o = ((y - region.y) * region.w + (x0 - region.x)) * 4;
            for (let x = x0; x < x1; x++, o += 4) {
                out.rgba[o] = 0;
                out.rgba[o + 1] = 0;
                out.rgba[o + 2] = 0;
                out.rgba[o + 3] = 0;
            }
        }
        return out;
    }
    /**
     * Composite layers bottom-to-top. Two independent axes (spuit-sample-modes-
     * 2026-06-22 §3):
     *
     *   - `respectVisibility`: skip hidden layers (display / spuit mode A) vs
     *     include every layer (export / snapshot — capture must not be skewed by
     *     one client's 👁 toggle, W3 §2.3). Also gates the display-only lead overlay.
     *   - `applyBg`: composite the canvas background under the stack (export /
     *     snapshot / spuit modes A & C) vs leave the raw transparent-default stack
     *     (display — the bg is emitted by CSS, 2026-06-20).
     *
     * The two axes were previously coupled (`applyBg = !respectVisibility`), which
     * only allowed (visible, no-bg) and (all, bg). Mode A needs the third
     * combination (visible, bg). Decoupling them keeps the existing two callers
     * bit-identical:
     *   - `compositeVisibleForDisplay` → `(true, false)` (was `compositeStack(true)`)
     *   - `compositeAllForExport`       → `(false, true)` (was `compositeStack(false)`)
     */
    compositeStack(respectVisibility, applyBg) {
        const layers = this.renderer.layers;
        const layer0 = layers[0];
        if (layer0 === undefined) {
            return new Uint8ClampedArray(new ArrayBuffer(0));
        }
        if (respectVisibility && !applyBg) {
            // True display path (visible, no bg): the shared region compositor over
            // the whole canvas — the ONLY stack that applies per-layer display
            // opacity. Mode A (visible + bg) and export (all + bg) fall through to
            // the loop below, which never reads `opacity`.
            const acc = this.composeDisplayLayers({
                x0: 0,
                y0: 0,
                x1: layer0.width - 1,
                y1: layer0.height - 1,
            });
            const shown = acc?.rgba ??
                new Uint8ClampedArray(new ArrayBuffer(layer0.width * layer0.height * 4));
            this.compositeLeadInto(shown, 0, 0, layer0.width, layer0.height);
            return shown;
        }
        let out = null;
        for (let id = 0; id < layers.length; id++) {
            if (respectVisibility && this.visibility[id] === false)
                continue;
            if (out === null) {
                if (applyBg) {
                    // Bottom-most included layer: bg + this layer (= `compositeForExport`).
                    out = this.compositeForExport(id);
                }
                else {
                    // bg hidden (display only): start from the raw (bg-free) layer
                    // composite so transparent pixels show through to nothing (= 透明).
                    // A copy is taken because `compositeForDisplay` may return
                    // `layer.main` directly, which must not be mutated by the overlay /
                    // source-over below.
                    const base = this.renderer.compositeForDisplay(id);
                    out = new Uint8ClampedArray(base);
                }
            }
            else {
                // Higher layer: source-over its (bg-free) layer composite onto the
                // accumulated stack (which already has the bg in it, or is bg-free).
                const upper = this.renderer.compositeForDisplay(id);
                sourceOverRegion(out, upper);
            }
            this.compositeOverlayInto(out, 0, 0, layer0.width, layer0.height, id);
        }
        if (out === null) {
            // No layers included (every layer hidden): just the bg (or transparent
            // when the bg is also hidden on the display path).
            out = new Uint8ClampedArray(new ArrayBuffer(layer0.width * layer0.height * 4));
            this.composeBackgroundInto(out, layer0.width, layer0.height, applyBg);
        }
        // 表示専用リード線 (§9.8.1) is composited only on the display path above
        // (handled at the top): capture / snapshot (all, bg) and spuit mode A
        // (visible, bg) sample the committed picture, never the transient lead.
        return out;
    }
    /**
     * Composite the canvas as it would be exported (= captured / snapshotted /
     * spuit-sampled): canvas background → each layer (α合成) → any in-flight
     * deferred-commit stroke buffers.
     *
     * Used by:
     *
     *   - `flush` / `repaint` to feed the display backend (so toDataURL on
     *     the display canvas naturally produces a bg-composited PNG — fixes
     *     the α 公開時に発覚した「キャプ画像で消しゴム跡が黒く焼き付く」症状).
     *   - `SnapshotEngine` to PNG-encode the per-layer state with the bg
     *     correctly burned in (snapshot bytes are the on-server source of
     *     truth, late-joiners must see the same bg the client sees).
     *   - `getLayerPixelColor` (spuit) so a click on a transparent / eraser-
     *     cleared region returns the bg color, not (0,0,0).
     *
     * Returns RGBA bytes (length = `width * height * 4`). When the bg is
     * fully transparent (alpha=0) AND no deferred strokes are in flight AND
     * `layerId` resolves to a layer, this could short-circuit and return
     * `layer.main`; we don't bother because the bg=transparent case is rare
     * and the small allocation is cheaper than the branching.
     *
     * `layerId` selects WHICH layer to composite over the bg. MVP always
     * passes 0 (the engine's only layer). β multi-layer can iterate this
     * helper per layer, or extend it to fold every layer into one buffer.
     */
    compositeForExport(layerId = 0) {
        const layer = this.renderer.layers[layerId];
        if (layer === undefined) {
            return new Uint8ClampedArray(new ArrayBuffer(0));
        }
        // 1. Start from the layer composite — includes any in-flight `'flat'` /
        //    `'marker'` stroke buffers. This already allocates a fresh buffer
        //    when there's an in-flight stroke; when there isn't, it returns
        //    `layer.main` directly (which we must NOT mutate, so we copy below).
        const layerComposite = this.renderer.compositeForDisplay(layerId);
        const out = new Uint8ClampedArray(new ArrayBuffer(layer.width * layer.height * 4));
        // 2. Paint bg into `out` first, then alpha-blend the layer composite on
        //    top. Standard "source over" (out = layer + bg * (1 - layer.alpha)).
        const bgR = (this.background.rgb >> 16) & 0xff;
        const bgG = (this.background.rgb >> 8) & 0xff;
        const bgB = this.background.rgb & 0xff;
        const bgA = this.background.alpha;
        for (let i = 0; i < out.length; i += 4) {
            const sr = layerComposite[i] ?? 0;
            const sg = layerComposite[i + 1] ?? 0;
            const sb = layerComposite[i + 2] ?? 0;
            const sa = layerComposite[i + 3] ?? 0;
            if (sa === 255) {
                // Fully opaque layer pixel — bg fully occluded.
                out[i] = sr;
                out[i + 1] = sg;
                out[i + 2] = sb;
                out[i + 3] = 255;
            }
            else if (sa === 0) {
                // Layer transparent — show bg as-is.
                out[i] = bgR;
                out[i + 1] = bgG;
                out[i + 2] = bgB;
                out[i + 3] = bgA;
            }
            else {
                // Partial alpha — source-over blend.
                const alpha = sa / 255;
                const bgAlpha = bgA / 255;
                const outA = alpha + bgAlpha * (1 - alpha);
                if (outA <= 0) {
                    out[i] = 0;
                    out[i + 1] = 0;
                    out[i + 2] = 0;
                    out[i + 3] = 0;
                }
                else {
                    out[i] = Math.round((sr * alpha + bgR * bgAlpha * (1 - alpha)) / outA);
                    out[i + 1] = Math.round((sg * alpha + bgG * bgAlpha * (1 - alpha)) / outA);
                    out[i + 2] = Math.round((sb * alpha + bgB * bgAlpha * (1 - alpha)) / outA);
                    out[i + 3] = Math.round(outA * 255);
                }
            }
        }
        return out;
    }
    /**
     * Composite **all** layers (id0 背 → id1 中 → id2 前) over the background
     * into a single full-canvas RGBA buffer — IGNORING display visibility.
     *
     * This is the canonical "export the whole drawing" view: capture
     * (camera) and any future client-side full-image export must use this
     * rather than reading the display canvas, because the display canvas may
     * have layers hidden via the local 👁 toggle, and capture / snapshot must
     * NOT be skewed by one client's per-layer view (W3 §2.3, task spec).
     *
     * The imagepaste overlay is applied on its target layer (so a paste
     * preview is captured the same way it's displayed). Returns RGBA bytes
     * (length = `width * height * 4`); empty buffer when there are no layers.
     */
    compositeAllForExport() {
        return this.compositeStack(false, true);
    }
    // ─── Phase 7 Task 7-8a — imagepaste overlay (preview) ────────────────────
    //
    // Drag perf hot-fix (2026-05-31, alpha-feedback-2026-05-31.md §5): the
    // overlay API is split into start / move / end so the rgba buffer only
    // crosses the worker boundary once (transferable, zero-copy), and drag
    // moves only ship two numbers. The previous single-method API
    // (`setImagepasteOverlay(overlay | null)`) was structuredClone-copying
    // ~8 MB / move for a 1920×1080 image at 60 Hz; the split eliminates that.
    /**
     * Get the current preview overlay (or null). For tests / the outer
     * `CanvasDrawingEngine`'s drag hit-test.
     */
    getImagepasteOverlay() {
        return this.overlay;
    }
    /**
     * Begin a new preview overlay. Replaces any previous overlay (which is
     * implicitly ended first); invalidates the union of old + new bounding
     * regions and repaints once. The overlay is **display only** until
     * `applyImagepaste` lands the actual broadcast (architecture.md §19,
     * Phase 7 Task 7-8a).
     *
     * The caller transfers ownership of `next.rgba` to the engine — the
     * worker boundary uses `postMessage(msg, [overlay.rgba.buffer])`, after
     * which the sender's view is detached. The engine retains the buffer
     * for the lifetime of the preview (= until `endImagepasteOverlay` /
     * `applyImagepaste` / a subsequent `startImagepasteOverlay`).
     */
    startImagepasteOverlay(next) {
        this.endLayerSolo(); // 強調と重ならない (overlay が優先)
        const prev = this.overlay;
        this.overlay = next;
        this.repaintImagepasteRegion(prev, next);
    }
    /**
     * Move the active preview to a new top-left position. No-op when no
     * preview is active (= a stale `move` after `end` from the main
     * thread). Reuses the retained rgba buffer; only the union region is
     * repainted.
     */
    moveImagepasteOverlay(x, y) {
        const prev = this.overlay;
        if (prev === null)
            return;
        if (prev.x === x && prev.y === y)
            return;
        const next = { ...prev, x, y };
        this.overlay = next;
        this.repaintImagepasteRegion(prev, next);
    }
    /**
     * Move the active preview to a different **target layer** (= imagepaste×
     * レイヤー実用化, A案 WYSIWYG, 2026-06-14). The overlay's pixels / position
     * are unchanged; only its **z position** in the display stack changes —
     * the preview re-composites on top of the new `layerId`'s pixels so the
     * user sees exactly where the paste will land once confirmed (= 前面レイヤー
     * 選択なら前面に、中間なら中間に見える).
     *
     * No-op when no preview is active (= a stale `setLayer` after `end` /
     * before `start`, or a call while the preview is locked-then-committed).
     *
     * Implementation: build the next overlay with the same rect but the new
     * `layerId`, then `repaintImagepasteRegion(prev, next)` over the overlay's
     * own rect. The union of prev/next rects is identical (same rect), and the
     * region repaint composites bg + EVERY visible layer + the overlay on its
     * (new) target layer via `compositeOverlayInto`'s `layerId` gate. So the
     * overlay disappears from the OLD z (it's no longer composited after the
     * old layer) and appears at the NEW z (composited after the new layer),
     * with the rest of the rect falling back to the true layer stack.
     * **Display only**: never writes `Layer.main`.
     */
    setImagepastePreviewLayer(layerId) {
        const prev = this.overlay;
        if (prev === null)
            return;
        if (prev.layerId === layerId)
            return;
        const next = { ...prev, layerId };
        this.overlay = next;
        this.repaintImagepasteRegion(prev, next);
    }
    /**
     * Clear the preview overlay and restore the underlying composite over
     * its former region. No-op when no preview is active.
     */
    endImagepasteOverlay() {
        const prev = this.overlay;
        if (prev === null)
            return;
        this.overlay = null;
        this.repaintImagepasteRegion(prev, null);
    }
    // ─── 表示専用リード線 (display-only lead, modern-layout.md §9.8.1 ①) ──────
    //
    // imagepaste preview overlay とまったく同じ流儀: 別バッファ (= `this.lead`) に
    // 保持し、display 合成 (`flush` / `repaint`) の最前面に重ねるだけ。`Layer.main`
    // には焼かない → stroke buffer 非汚染が構造的に保証される。前フレームのリードは
    // overlay 自身の bbox を dirty に積んで display 再合成させることで消える。
    /**
     * Rasterize the lead's short gap point list through the SAME stamp machinery
     * the committed stroke uses (`rasterizeLeadSegment` → `drawDot`/`drawLine` →
     * `stampOnce`/`stampOnceAA`) and stash the straight-α RGBA as the current
     * lead overlay. Repaints the union of the previous + new lead rects.
     *
     * **Display-only**: never writes `Layer.main`, never touches the renderer's
     * `accumulatedDirty` / commit path (the transient buffer in
     * `rasterizeLeadSegment` is fully separate). The committed body is unchanged;
     * the canonical (sent) geometry is the smoothed curve.
     *
     * No-op-equivalent inputs (eraser pen / empty points / zero coverage) clear
     * the lead instead (= ゼロ長リードは非表示、補正 OFF / Classic で no-op)。
     */
    setLead(args) {
        const layer = this.renderer.layers[args.layerId];
        if (layer === undefined) {
            this.clearLead();
            return;
        }
        const next = rasterizeLeadSegment({
            pen: args.pen,
            layerId: args.layerId,
            layerWidth: layer.width,
            layerHeight: layer.height,
            // mask compare reads the live main (= what the committed stroke would
            // also gate against). The lead is a hint so this is best-effort parity.
            mainRgba: args.mask !== undefined && args.mask.type !== 'none' ? layer.main : undefined,
            ...(args.mask !== undefined ? { mask: args.mask } : {}),
            points: args.points,
        });
        const prev = this.lead;
        this.lead = next;
        this.repaintLeadRegion(prev, next);
    }
    /** Clear the lead overlay and restore the underlying composite over its rect. */
    clearLead() {
        const prev = this.lead;
        if (prev === null)
            return;
        this.lead = null;
        this.repaintLeadRegion(prev, null);
    }
    /** Current lead overlay (or null). For tests. */
    getLead() {
        return this.lead;
    }
    /**
     * Repaint the union of the previous + next lead rects (= the dirty area the
     * lead changed). Mirrors `repaintImagepasteRegion`: composites bg + visible
     * layers + imagepaste overlay + the lead (frontmost) for the affected
     * sub-rect and blits it. Restores cleanly when the new lead is `null`.
     */
    repaintLeadRegion(prev, next) {
        if (this.backend === null || this.displayHeld())
            return;
        const region = unionRect(prev !== null ? leadRect(prev) : null, next !== null ? leadRect(next) : null);
        if (region === null)
            return;
        const layer0 = this.renderer.layers[0];
        if (layer0 === undefined)
            return;
        const x0 = Math.max(0, region.x);
        const y0 = Math.max(0, region.y);
        const x1 = Math.min(layer0.width - 1, region.x + region.w - 1);
        const y1 = Math.min(layer0.height - 1, region.y + region.h - 1);
        if (x1 < x0 || y1 < y0)
            return;
        const bbox = { x0, y0, x1, y1 };
        // Build the display stack (bg + every visible layer + imagepaste overlay)
        // for this sub-rect, then composite the lead on top.
        let acc = this.composeDisplayLayers(bbox);
        if (acc === null) {
            const w = x1 - x0 + 1;
            const h = y1 - y0 + 1;
            // Bg-free transparent base (display only); CSS background shows through.
            const buf = new Uint8ClampedArray(new ArrayBuffer(w * h * 4));
            acc = { rgba: buf, x: x0, y: y0, w, h };
        }
        this.compositeLeadInto(acc.rgba, acc.x, acc.y, acc.w, acc.h);
        this.backend.put(acc.rgba, acc.x, acc.y, acc.w, acc.h);
    }
    /**
     * Composite the lead overlay (frontmost) onto a `subset` buffer that already
     * holds the display stack for `(dx, dy, w, h)`. Straight-α non-premultiplied
     * source-over — identical math to `compositeOverlayInto` (imagepaste). No-op
     * when there's no lead or it doesn't intersect the region.
     *
     * The seam with the committed pixels is a single source-over here (the lead's
     * straight-α coverage over the body), so half-transparent (build-up) pens get
     * at most one extra alpha accumulation at the gap — never a double-alpha焼き
     * (the lead is never written to `Layer.main`, §9.8.1 半透明継ぎ目).
     */
    compositeLeadInto(subset, dx, dy, w, h) {
        const lead = this.lead;
        if (lead === null)
            return;
        // The lead belongs to its stroke's layer: dim it with that layer so the
        // confirmed stroke doesn't pop darker when the lead collapses.
        const opacity = this.opacity[lead.layerId] ?? 1;
        const lx = lead.x;
        const ly = lead.y;
        const ix0 = Math.max(dx, lx);
        const iy0 = Math.max(dy, ly);
        const ix1 = Math.min(dx + w - 1, lx + lead.width - 1);
        const iy1 = Math.min(dy + h - 1, ly + lead.height - 1);
        if (ix1 < ix0 || iy1 < iy0)
            return;
        for (let y = iy0; y <= iy1; y++) {
            for (let x = ix0; x <= ix1; x++) {
                const sx = x - lx;
                const sy = y - ly;
                const srcIdx = (sy * lead.width + sx) * 4;
                const sa = opacity === 1
                    ? (lead.rgba[srcIdx + 3] ?? 0)
                    : Math.round((lead.rgba[srcIdx + 3] ?? 0) * opacity);
                if (sa === 0)
                    continue;
                const dstIdx = ((y - dy) * w + (x - dx)) * 4;
                const sr = lead.rgba[srcIdx] ?? 0;
                const sg = lead.rgba[srcIdx + 1] ?? 0;
                const sb = lead.rgba[srcIdx + 2] ?? 0;
                if (sa === 255) {
                    subset[dstIdx] = sr;
                    subset[dstIdx + 1] = sg;
                    subset[dstIdx + 2] = sb;
                    subset[dstIdx + 3] = 255;
                    continue;
                }
                blendStraightAlpha(subset, dstIdx, sr, sg, sb, sa);
            }
        }
    }
    /**
     * Internal: repaint the union of the old + new overlay rectangles.
     * Pulled out of the previous single-method API so start / move / end
     * share one implementation (= same on-screen behaviour).
     *
     * 正しさ + perf 修正 (2026-06-14, imagepaste×レイヤー実用化で顕在):
     * the old body built the underlay with `compositeForExport(layerId)`, which
     * is (a) a FULL-CANVAS allocate+composite每 move (= 4000² で 64MB 級 pixel 処理
     * → ドラッグカクつき) and (b) **bg + that single layer only** — so dragging the
     * overlay (always layerId 0) tore holes in whatever was drawn on layers 1/2/3
     * (those layers were absent from the underlay, so the overlay's vacated trail
     * lost them).
     *
     * Now it mirrors `repaintLeadRegion` / `flush`: composite bg + EVERY visible
     * layer (stack 順) + the imagepaste overlay (on its target layer, via
     * `compositeOverlayInto`'s `layerId` gate so it lands EXACTLY once) + the
     * display-only lead — all restricted to the union sub-rect. `this.overlay` is
     * already `next` by the time we're called (start/move/end set it first), so
     * the loop paints the overlay at its new spot and the vacated area falls back
     * to the true layer stack. **Display only**: never writes `Layer.main`.
     */
    repaintImagepasteRegion(prev, next) {
        if (this.backend === null || this.displayHeld())
            return;
        const layer0 = this.renderer.layers[0];
        if (layer0 === undefined)
            return;
        // The union must cover BOTH the old and new overlay rects regardless of
        // their layerId (a start can replace an overlay on a different layer), so
        // the vacated area on either layer is correctly restored.
        const region = unionRect(prev !== null ? rectOf(prev) : null, next !== null ? rectOf(next) : null);
        if (region === null)
            return;
        const x0 = Math.max(0, region.x);
        const y0 = Math.max(0, region.y);
        const x1 = Math.min(layer0.width - 1, region.x + region.w - 1);
        const y1 = Math.min(layer0.height - 1, region.y + region.h - 1);
        if (x1 < x0 || y1 < y0)
            return;
        const bbox = { x0, y0, x1, y1 };
        // Region-limited display stack: bg + every visible layer + the imagepaste
        // overlay (on its target layer) — same construction as `flush` /
        // `repaintLeadRegion`. Cost is O(bbox area × visibleLayerCount), never the
        // full canvas (the perf fix), and every visible layer is present (the
        // correctness fix — no more torn trail on layers 1/2/3).
        let acc = this.composeDisplayLayers(bbox);
        if (acc === null) {
            // Every layer in the region is hidden / empty: a transparent base (display
            // only, bg-free) so the overlay's vacated area doesn't keep showing stale
            // pixels. The CSS background shows through the cleared region.
            const w = x1 - x0 + 1;
            const h = y1 - y0 + 1;
            const buf = new Uint8ClampedArray(new ArrayBuffer(w * h * 4));
            acc = { rgba: buf, x: x0, y: y0, w, h };
        }
        // 表示専用リード線 (§9.8.1): frontmost, display only — keep it visible when an
        // overlay repaint overlaps the lead rect (= same as `flush`).
        this.compositeLeadInto(acc.rgba, acc.x, acc.y, acc.w, acc.h);
        this.backend.put(acc.rgba, acc.x, acc.y, acc.w, acc.h);
    }
    /**
     * Commit a `whiteboard.imagepaste` broadcast: stamp the bitmap into
     * `Layer.main` (opaque overwrite) and clear any preview overlay. The
     * affected region is repainted through the normal flush path.
     *
     * `rgba` is RGBA bytes; `width * height * 4` MUST match the buffer
     * length (mismatched payloads are silently dropped, mirroring
     * `applySnapshot`).
     *
     * Used by both the browser engine (after fetching the presigned URL)
     * and the server-side snapshot engine (for late-join replay, when that
     * lands in Phase 8+).
     */
    applyImagepaste(args) {
        if (args.rgba.length !== args.width * args.height * 4)
            return;
        const layer = this.renderer.layers[args.layerId];
        if (layer === undefined)
            return;
        // Drop the preview overlay unconditionally: a commit means this paste is
        // final, so its display-only preview has served its purpose. Gating on
        // `overlay.layerId === args.layerId` left a ghost when the two diverged —
        // the preview overlay is always layerId 0 (the client doesn't pass a
        // layerId to `startImagepastePreview`), but `commit.layerId` is now the
        // selected layer (e.g. 3), so the overlay was never dropped (real bug).
        // The repaint after the layer write (or the off-canvas early return)
        // re-composites the region without the overlay, wiping the ghost.
        if (this.overlay !== null) {
            this.overlay = null;
        }
        // Clamp the destination rectangle to the layer bounds.
        const dstX0 = Math.max(0, Math.floor(args.x));
        const dstY0 = Math.max(0, Math.floor(args.y));
        const dstX1 = Math.min(layer.width - 1, Math.floor(args.x) + args.width - 1);
        const dstY1 = Math.min(layer.height - 1, Math.floor(args.y) + args.height - 1);
        if (dstX1 < dstX0 || dstY1 < dstY0) {
            // Entirely off-canvas — still trigger a repaint so any leftover
            // overlay is wiped.
            if (this.backend !== null)
                this.repaint();
            return;
        }
        const srcOffsetX = dstX0 - Math.floor(args.x);
        const srcOffsetY = dstY0 - Math.floor(args.y);
        const w = dstX1 - dstX0 + 1;
        const h = dstY1 - dstY0 + 1;
        for (let y = 0; y < h; y++) {
            const srcRowStart = ((srcOffsetY + y) * args.width + srcOffsetX) * 4;
            const dstRowStart = ((dstY0 + y) * layer.width + dstX0) * 4;
            for (let x = 0; x < w; x++) {
                const sr = args.rgba[srcRowStart + x * 4] ?? 0;
                const sg = args.rgba[srcRowStart + x * 4 + 1] ?? 0;
                const sb = args.rgba[srcRowStart + x * 4 + 2] ?? 0;
                const sa = args.rgba[srcRowStart + x * 4 + 3] ?? 0;
                if (sa === 255) {
                    // Fast path: opaque source overwrites destination.
                    layer.main[dstRowStart + x * 4] = sr;
                    layer.main[dstRowStart + x * 4 + 1] = sg;
                    layer.main[dstRowStart + x * 4 + 2] = sb;
                    layer.main[dstRowStart + x * 4 + 3] = 255;
                }
                else if (sa > 0) {
                    // Non-premultiplied source-over alpha-blend onto the existing
                    // layer pixel. PNGs with transparency (e.g. user-uploaded PNGs
                    // that aren't fully opaque) hit this branch.
                    //
                    // 2026-05-17 (layer/background separation): use the standard
                    // `outA = sa + da * (1-sa)` formula + RGB normalisation by
                    // `outA` so the result composites correctly under the bg later.
                    // The previous formula (`out = src*α + dst*(1-α)` + `outA =
                    // max(sa, da)`) produced premultiplied-style output that was
                    // visually correct only because the bg was baked into `dst.a`.
                    // With layer/background separation the layer can be transparent
                    // and the bg lives below, so the math must be normalised.
                    const dr = layer.main[dstRowStart + x * 4] ?? 0;
                    const dg = layer.main[dstRowStart + x * 4 + 1] ?? 0;
                    const db = layer.main[dstRowStart + x * 4 + 2] ?? 0;
                    const da = layer.main[dstRowStart + x * 4 + 3] ?? 0;
                    const aSrc = sa / 255;
                    const aDst = da / 255;
                    const aOut = aSrc + aDst * (1 - aSrc);
                    if (aOut <= 0) {
                        // Both src + dst transparent → output transparent. Defensive;
                        // we already early-returned on `sa === 0`.
                        layer.main[dstRowStart + x * 4] = 0;
                        layer.main[dstRowStart + x * 4 + 1] = 0;
                        layer.main[dstRowStart + x * 4 + 2] = 0;
                        layer.main[dstRowStart + x * 4 + 3] = 0;
                    }
                    else {
                        layer.main[dstRowStart + x * 4] = Math.round((sr * aSrc + dr * aDst * (1 - aSrc)) / aOut);
                        layer.main[dstRowStart + x * 4 + 1] = Math.round((sg * aSrc + dg * aDst * (1 - aSrc)) / aOut);
                        layer.main[dstRowStart + x * 4 + 2] = Math.round((sb * aSrc + db * aDst * (1 - aSrc)) / aOut);
                        layer.main[dstRowStart + x * 4 + 3] = Math.round(aOut * 255);
                    }
                }
            }
        }
        this.renderer.markLayerDirty(args.layerId, { x0: dstX0, y0: dstY0, x1: dstX1, y1: dstY1 });
        // Repaint the union of overlay region + commit region. The cheapest
        // correct thing (and the imagepaste path is rare) is a full repaint.
        if (this.backend !== null)
            this.repaint();
        this.thumbs?.kick();
    }
    /**
     * Composite the active imagepaste overlay onto a `subset` buffer that
     * already holds the layer composite for `(dx, dy, w, h)`. No-op when
     * there is no overlay or the overlay doesn't intersect the region.
     */
    compositeOverlayInto(subset, dx, dy, w, h, layerId, opacity = 1) {
        const overlay = this.overlay;
        if (overlay === null || overlay.layerId !== layerId)
            return;
        const ox = Math.floor(overlay.x);
        const oy = Math.floor(overlay.y);
        // Intersection of overlay rect with subset rect, in layer-space.
        const ix0 = Math.max(dx, ox);
        const iy0 = Math.max(dy, oy);
        const ix1 = Math.min(dx + w - 1, ox + overlay.width - 1);
        const iy1 = Math.min(dy + h - 1, oy + overlay.height - 1);
        if (ix1 < ix0 || iy1 < iy0)
            return;
        for (let y = iy0; y <= iy1; y++) {
            for (let x = ix0; x <= ix1; x++) {
                const sx = x - ox;
                const sy = y - oy;
                const srcIdx = (sy * overlay.width + sx) * 4;
                const dstIdx = ((y - dy) * w + (x - dx)) * 4;
                const sa = opacity === 1
                    ? (overlay.rgba[srcIdx + 3] ?? 0)
                    : Math.round((overlay.rgba[srcIdx + 3] ?? 0) * opacity);
                if (sa === 0)
                    continue;
                const sr = overlay.rgba[srcIdx] ?? 0;
                const sg = overlay.rgba[srcIdx + 1] ?? 0;
                const sb = overlay.rgba[srcIdx + 2] ?? 0;
                if (sa === 255) {
                    subset[dstIdx] = sr;
                    subset[dstIdx + 1] = sg;
                    subset[dstIdx + 2] = sb;
                    subset[dstIdx + 3] = 255;
                    continue;
                }
                blendStraightAlpha(subset, dstIdx, sr, sg, sb, sa);
            }
        }
    }
}
const rectOf = (overlay) => ({
    x: Math.floor(overlay.x),
    y: Math.floor(overlay.y),
    w: overlay.width,
    h: overlay.height,
});
/** Lead overlay rect (already integer layer-space coords from `rasterizeLeadSegment`). */
const leadRect = (lead) => ({
    x: lead.x,
    y: lead.y,
    w: lead.width,
    h: lead.height,
});
const unionRect = (a, b) => {
    if (a === null)
        return b;
    if (b === null)
        return a;
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const x1 = Math.max(a.x + a.w, b.x + b.w);
    const y1 = Math.max(a.y + a.h, b.y + b.h);
    return { x, y, w: x1 - x, h: y1 - y };
};
/**
 * In-place non-premultiplied source-over of `src` (upper layer) onto `dst`
 * (the accumulated lower stack, which already has the bg composited in).
 * Both buffers are the same RGBA dimensions. Used by the multi-layer
 * display + export composite (W3 Classic レイヤー追加).
 *
 * Standard formula: `outA = sa + da*(1-sa)`, RGB normalised by `outA`. This
 * is identical to the source-over `compositeForExport`/`applyImagepaste`
 * use, so a single opaque upper layer fully replaces the lower stack and a
 * transparent upper layer leaves it untouched.
 */
const sourceOverRegion = (dst, src, opacity = 1) => {
    const len = Math.min(dst.length, src.length);
    for (let i = 0; i < len; i += 4) {
        const sa = opacity === 1 ? (src[i + 3] ?? 0) : Math.round((src[i + 3] ?? 0) * opacity);
        if (sa === 0)
            continue; // upper transparent — lower stack shows through
        const sr = src[i] ?? 0;
        const sg = src[i + 1] ?? 0;
        const sb = src[i + 2] ?? 0;
        if (sa === 255) {
            // Upper opaque — fully occludes the lower stack.
            dst[i] = sr;
            dst[i + 1] = sg;
            dst[i + 2] = sb;
            dst[i + 3] = 255;
            continue;
        }
        const dr = dst[i] ?? 0;
        const dg = dst[i + 1] ?? 0;
        const db = dst[i + 2] ?? 0;
        const da = (dst[i + 3] ?? 0) / 255;
        const aSrc = sa / 255;
        const outA = aSrc + da * (1 - aSrc);
        if (outA <= 0) {
            dst[i] = 0;
            dst[i + 1] = 0;
            dst[i + 2] = 0;
            dst[i + 3] = 0;
            continue;
        }
        dst[i] = Math.round((sr * aSrc + dr * da * (1 - aSrc)) / outA);
        dst[i + 1] = Math.round((sg * aSrc + dg * da * (1 - aSrc)) / outA);
        dst[i + 2] = Math.round((sb * aSrc + db * da * (1 - aSrc)) / outA);
        dst[i + 3] = Math.round(outA * 255);
    }
};
/**
 * Straight-α source-over of one (sr,sg,sb,sa) pixel onto `dst[i..i+3]`, with
 * the same math as `sourceOverRegion` (the old overlay / lead blend mixed the
 * colour with the destination RGB even where the destination was transparent,
 * which darkened a half-α pixel over empty canvas — visible once an overlay is
 * dimmed by layer opacity, and a pop when it is later committed).
 */
const blendStraightAlpha = (dst, i, sr, sg, sb, sa) => {
    const aSrc = sa / 255;
    const da = (dst[i + 3] ?? 0) / 255;
    const outA = aSrc + da * (1 - aSrc);
    if (outA <= 0) {
        dst[i] = 0;
        dst[i + 1] = 0;
        dst[i + 2] = 0;
        dst[i + 3] = 0;
        return;
    }
    dst[i] = Math.round((sr * aSrc + (dst[i] ?? 0) * da * (1 - aSrc)) / outA);
    dst[i + 1] = Math.round((sg * aSrc + (dst[i + 1] ?? 0) * da * (1 - aSrc)) / outA);
    dst[i + 2] = Math.round((sb * aSrc + (dst[i + 2] ?? 0) * da * (1 - aSrc)) / outA);
    dst[i + 3] = Math.round(outA * 255);
};
/** Multiply every pixel's alpha by `opacity` (0..1) in place. Caller guarantees `buf` is owned. */
const scaleAlphaInPlace = (buf, opacity) => {
    for (let i = 3; i < buf.length; i += 4) {
        const a = buf[i] ?? 0;
        if (a !== 0)
            buf[i] = Math.round(a * opacity);
    }
};
/**
 * Clamp an inclusive dirty bbox to layer bounds, returning a top-left + size
 * rect, or null when the bbox doesn't intersect the layer. Mirrors the
 * clamping `compositeForDisplayRegion` does internally — used by `flush`'s
 * all-hidden fallback to paint just the bg over the dirty sub-rect.
 */
/**
 * 矩形 (x, y, w, h) を層 (layerW × layerH) に切り捨てる。範囲の移動の命令は整数で来る
 * 前提 (防御として floor)。完全に範囲外 / 空 / 非有限なら null。
 */
const clampRegionToLayer = (x, y, w, h, layerW, layerH) => {
    if (!Number.isFinite(x + y + w + h))
        return null;
    const fx = Math.floor(x);
    const fy = Math.floor(y);
    const fw = Math.floor(w);
    const fh = Math.floor(h);
    if (fw <= 0 || fh <= 0)
        return null;
    return clampBboxToLayer({ x0: fx, y0: fy, x1: fx + fw - 1, y1: fy + fh - 1 }, layerW, layerH);
};
const clampBboxToLayer = (bbox, width, height) => {
    const x0 = bbox.x0 < 0 ? 0 : bbox.x0;
    const y0 = bbox.y0 < 0 ? 0 : bbox.y0;
    const x1 = bbox.x1 > width - 1 ? width - 1 : bbox.x1;
    const y1 = bbox.y1 > height - 1 ? height - 1 : bbox.y1;
    if (x0 > x1 || y0 > y1)
        return null;
    return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
};
//# sourceMappingURL=engineCore.js.map